<template>
  <PopupBase
    :show="show"
    :class="['product-cure-date-popup', { 'single-item': groupedBatchData.length === 1 }]"
    @close-pop-up="$emit('close')"
  >
    <h4>{{ $tv('cureDate', 'product') }}</h4>
    <div class="popup-content">
      <div class="ecom-text">
        <strong>{{ ecomText }}</strong>
      </div>
      <div class="item-no-row">
        <span class="item-no">{{ $tv('fstItemNo', 'cart') }}</span> <strong>{{ product?.sku }}</strong>
      </div>
      <h4 class="current-inventory">
        {{ $tv('availableItems', 'product') }}
      </h4>
      <div class="batch-data-area">
        <div
          v-if="groupedBatchData.length >= 4"
          class="slider-container-wrapper"
        >
          <div class="slider-container">
            <div
              class="slider-wrapper"
              :style="{ transform: `translateX(-${sliderOffset}px)` }"
            >
              <CureDatePlantTable
                v-for="(plant, idx) in groupedBatchData"
                :key="idx"
                :plant-name="plant.plant"
                :total-quantity="plant.stock.reduce((sum, s) => sum + (s.quantity || 0), 0)"
                :items="plant.stock"
                :total-groups="groupedBatchData.length"
                :is-default-warehouse="plant.isDefaultWarehouse"
                class="slider-item"
              />
            </div>
          </div>
          <NavigationChevronLeft
            v-if="canSlidePrev"
            class="slider-icon slider-icon--prev"
            @click="slidePrev"
          />
          <NavigationChevronRight
            v-if="canSlideNext"
            class="slider-icon slider-icon--next"
            @click="slideNext"
          />
        </div>
        <div
          v-else-if="groupedBatchData.length === 1"
          class="single-item-container"
        >
          <CureDatePlantTable
            v-for="(plant, idx) in groupedBatchData"
            :key="idx"
            :plant-name="plant.plant"
            :total-quantity="plant.stock.reduce((sum, s) => sum + (s.quantity || 0), 0)"
            :items="plant.stock"
            :total-groups="groupedBatchData.length"
            :is-default-warehouse="plant.isDefaultWarehouse"
          />
        </div>
        <template v-else>
          <CureDatePlantTable
            v-for="(plant, idx) in groupedBatchData"
            :key="idx"
            :plant-name="plant.plant"
            :total-quantity="plant.stock.reduce((sum, s) => sum + (s.quantity || 0), 0)"
            :items="plant.stock"
            :total-groups="groupedBatchData.length"
            :is-default-warehouse="plant.isDefaultWarehouse"
          />
        </template>
      </div>
      <div
        v-if="groupedBatchData.length > 1"
        class="default-warehouse-note"
      >
        {{ $tv('default-warehouse', 'product') }}
      </div>
    </div>
  </PopupBase>
</template>

<script setup lang="ts">
import PopupBase from '~/components/popup/PopupBase'
import CureDatePlantTable from '~/components/CureDatePlantTable.vue'
import NavigationChevronLeft from '~/assets/icons/navigation_chevron_left.svg?component'
import NavigationChevronRight from '~/assets/icons/navigation_chevron_right.svg?component'

// Types
interface BatchDataItem {
  plant: string
  batchNumber: string
  quantity: number
  productionDate: string
  isDefaultWarehouse?: boolean
}

interface Product {
  sku: string
  [key: string]: any
}

interface GroupedBatchItem {
  plant: string
  isDefaultWarehouse?: boolean
  stock: Array<{
    quantity: number
    productionDate: string
  }>
}

// Props
const props = defineProps<{
  show: boolean
  product: Product
  ecomText?: string
  batchData?: BatchDataItem[] | null
}>()

// Emits
defineEmits<{
  close: []
}>()

// Reactive data
const sliderOffset = ref<number>(0)
const itemWidth: number = 277 // 15.25rem (244px) + 1rem gap (16px) + 1rem padding (16px) + .125rem border (1px) = 277px

// Computed properties
const groupedBatchData = computed<GroupedBatchItem[]>(() => {
  // Groups batchData by plant and returns array with stock array for each plant
  if (!Array.isArray(props.batchData)) { return [] }
  const map: Record<string, GroupedBatchItem> = {}
  props.batchData.forEach((item: BatchDataItem) => {
    if (!map[item.plant]) {
      map[item.plant] = {
        plant: item.plant,
        isDefaultWarehouse: item.isDefaultWarehouse,
        stock: []
      }
    }
    map[item.plant].stock.push({
      quantity: item.quantity,
      productionDate: item.productionDate
    })
  })
  Object.keys(map).forEach(key => {
    const item = map[key]
    // Ensure stock is sorted by production date
    item.stock.sort((a, b) => {
      const dateA = new Date(a.productionDate)
      const dateB = new Date(b.productionDate)
      return dateA.getTime() - dateB.getTime()
    })
  })
  // Sort by default warehouse first, then alphabetically by plant name
  return Object.values(map).sort((a, b) => {
    // Default warehouses come first
    if (a.isDefaultWarehouse && !b.isDefaultWarehouse) { return -1 }
    if (!a.isDefaultWarehouse && b.isDefaultWarehouse) { return 1 }
    // If both are default or both are not default, sort alphabetically by plant name
    return a.plant.localeCompare(b.plant)
  })
})

const canSlideNext = computed<boolean>(() => {
  const totalItems: number = groupedBatchData.value.length
  const visibleItems: number = 3
  const maxSlides: number = Math.max(0, totalItems - visibleItems)
  const currentSlide: number = sliderOffset.value / itemWidth
  return currentSlide < maxSlides
})

const canSlidePrev = computed<boolean>(() => {
  return sliderOffset.value > 0
})

// Methods
const slideNext = (): void => {
  if (canSlideNext.value) {
    const totalItems: number = groupedBatchData.value.length
    const visibleItems: number = 3
    const slideBy: number = 3
    const maxOffset: number = Math.max(0, totalItems - visibleItems) * itemWidth
    sliderOffset.value = Math.min(sliderOffset.value + (slideBy * itemWidth), maxOffset)
  }
}

const slidePrev = (): void => {
  if (canSlidePrev.value) {
    const slideBy: number = 3
    sliderOffset.value = Math.max(0, sliderOffset.value - (slideBy * itemWidth))
  }
}
</script>
<style lang="scss">
.product-cure-date-popup {
  .popup {
    .popup__wrapper {
      max-width: unset;
      width: fit-content;
    }
  }

  &.single-item {
    .popup {
      .popup__wrapper {
        width: 33.75rem; // 540px converted to rem (540px ÷ 16px = 33.75rem)
      }
    }
  }

  // Mobile fullscreen override at $sm breakpoint
  @include breakpoint-down($sm) {
    .popup {
      .popup__wrapper {
        margin: 0;
        inset: 0;
        min-height: 100vh;
        min-width: 100vw;
        width: 100vw;
        height: 100vh;
        max-width: none;
        overflow-y: auto;
        display: flex;
        flex-direction: column;

        .popup__content {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
        }
      }
    }
  }

  h4 {
    margin: -.5rem 0 1rem;

    @include breakpoint-down($md) {
      margin: 0 0 1rem;
    }
  }

  .item-no {
    font-size: .875rem;
    font-weight: $base-text-font-weight-light;
    margin-right: .25rem;
  }

  .current-inventory {
    margin-top: 1.625rem;
  }

  @include breakpoint-down($sm) {
    .item-no-row {
      display: none;
    }
  }

  .item-no-row strong {
    font-size: 1rem;
  }

  .ecom-text {
    font-size: 1rem;
    margin-bottom: .625rem;

    strong {
      font-weight: $base-text-font-weight-semi-bold;
    }

    // Truncate text on mobile
    @include breakpoint-down($sm) {
      strong {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: calc(100vw - 2rem);
      }
    }
  }

  .default-warehouse-note {
    font-size: .875rem;
    font-weight: $base-text-font-weight-light;
    margin-top: 1rem;

    // Hide on mobile since each individual table shows it
    @include breakpoint-down($md) {
      display: none;
    }
  }

  .batch-data-area {
    margin-top: 1rem;
    display: flex;
    flex-direction: row;
    gap: 1rem;

    // Border styling for non-slider layout
    &:not(:has(.slider-container-wrapper)) > *:not(:last-child) {
      padding-right: 1rem;
      border-right: .125rem solid $color--input__border;
    }

    // Mobile responsive: Stack items vertically and hide slider
    @include breakpoint-down($md) {
      flex-direction: column;
      align-items: center;
      gap: 1rem;

      // Remove borders and padding on mobile
      &:not(:has(.slider-container-wrapper)) > *:not(:last-child) {
        padding-right: 0;
        border-right: none;
      }

      // On mobile, show slider items stacked instead of hiding wrapper
      .slider-container-wrapper {
        position: static;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        width: 100%;

        .slider-container {
          max-width: none;
          overflow: visible;
          width: 100%;
        }

        .slider-wrapper {
          flex-direction: column;
          align-items: center;
          transform: none !important;
          gap: 1rem;
        }

        .slider-item {
          border-right: none;
          padding-right: 0;
          min-width: auto;
        }
      }
    }
  }

  .single-item-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .slider-container-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    gap: .25rem;
  }

  .slider-container {
    width: 100%;
    overflow: hidden;
    max-width: calc(3 * 15.25rem + 2 * 1rem + 2 * 1rem + 2 * .125rem); // 3 items + 2 gaps + 2 paddings + 2 borders
    order: 1;
  }

  .slider-wrapper {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    transition: transform 0.3s ease;
  }

  .slider-item {
    flex-shrink: 0;
    min-width: 15.25rem;
    padding-right: 1rem;
    border-right: .125rem solid $color--input__border;

    &:last-child {
      border-right: none;
      padding-right: 0;
    }
  }

  .slider-icon {
    width: 2rem;
    height: 2rem;
    color: $color__primary;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.3s ease;

    &:hover {
      color: darken($color__primary, 10%);
    }

    // Hide slider controls on mobile
    @include breakpoint-down($md) {
      display: none;
    }
  }

  .slider-icon--prev {
    order: 0;
  }

  .slider-icon--next {
    order: 2;
  }

  .slide-btn-enter-active,
  .slide-btn-leave-active {
    transition: all 0.3s ease;
  }

  .slide-btn-enter-from {
    width: 0;
    height: 0;
    opacity: 0;
  }

  .slide-btn-leave-to {
    width: 0;
    height: 0;
    opacity: 0;
  }

  .slide-btn-enter-to,
  .slide-btn-leave-from {
    width: 2rem;
    height: 2rem;
    opacity: 1;
  }
}
</style>
